import { EntityMap, mergeRecord } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import { GroupMemberPanelsLoadFulfilledAction } from '../../domains/groupMemberPanels/actions.types';
import { GroupMemberSearchLoadFulfilledAction } from '../../domains/groupMemberSearch/actions.types';
import {
  LeaderLoadFulfilledAction,
  NewLeadersSuggestionsLoadFulfilledAction,
  UserConnectedLeadersListLoadFulfilledAction,
  UserConnectedLeadersListLoadMoreFulfilledAction,
  UserFollowedLeadersListLoadFulfilledAction,
  UserFollowedLeadersListLoadMoreFulfilledAction,
  UserNetworkLeadersListLoadFulfilledAction,
  UserNetworkLeadersListLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserMomentMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import { SearchLoadFulfilledAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import {
  SuggestedLeadersLoadFulfilledAction,
  SuggestedLeadersLoadMoreFulfilledAction,
} from '../../domains/suggestedLeaders/actions.types';
import { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import { UserHeaderLoadFulfilledAction } from '../../domains/userHeaderLoad/actions.types';

import { UserOriginNetwork, UserOriginNetworks } from './types';
export type UserOriginNetworkAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserMomentMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | LeaderLoadFulfilledAction
  | UserConnectedLeadersListLoadFulfilledAction
  | UserConnectedLeadersListLoadMoreFulfilledAction
  | UserFollowedLeadersListLoadFulfilledAction
  | UserFollowedLeadersListLoadMoreFulfilledAction
  | UserNetworkLeadersListLoadFulfilledAction
  | UserNetworkLeadersListLoadMoreFulfilledAction
  | SuggestedLeadersLoadFulfilledAction
  | SuggestedLeadersLoadMoreFulfilledAction
  | UserHeaderLoadFulfilledAction
  | SearchLoadFulfilledAction
  | GroupMemberPanelsLoadFulfilledAction
  | GroupMemberSearchLoadFulfilledAction
  | NewLeadersSuggestionsLoadFulfilledAction
  | TypeaheadFulfilledAction;

const initialState: UserOriginNetworks = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<UserOriginNetworks, UserOriginNetworkAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'LEADER.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'FOLLOWED_LEADERS.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'FOLLOWED_LEADERS.LOAD_MORE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'CONNECTED_LEADERS.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'CONNECTED_LEADERS.LOAD_MORE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'SUGGESTED_LEADERS.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'SUGGESTED_LEADERS.LOAD_MORE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'FEED.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'USER_HEADER.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'SEARCH.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'GROUP_MEMBER_PANELS.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'GROUP_MEMBER_SEARCH.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'NEW_LEADERS_SUGGESTIONS.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return addOrUpdateUserOriginNetwork(state, action.payload.entities.userOriginNetworks);
    default:
      return state;
  }
};

const omitUndefinedValues = (obj: UserOriginNetwork): Partial<UserOriginNetwork> =>
  Object.entries(obj).reduce((target, [key, value]) => {
    if (value !== undefined) {
      return { ...target, [key]: value };
    }

    return target;
  }, {});

const addOrUpdateUserOriginNetwork = (
  state: EntityMap<UserOriginNetwork>,
  userOriginNetworks: Record<string, UserOriginNetwork> | undefined
): EntityMap<UserOriginNetwork> => {
  if (!userOriginNetworks) {
    return state;
  }

  const newRecords: Record<string, UserOriginNetwork> = Object.entries(userOriginNetworks).reduce(
    (acc, [userId, userOriginNetwork]) => {
      const sanitizedUserOriginNetwork = omitUndefinedValues(userOriginNetwork);

      return {
        ...acc,
        ...mergeRecord(state.fulfilled.byId, userId, sanitizedUserOriginNetwork),
      };
    },
    {}
  );

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      byId: {
        ...state.fulfilled.byId,
        ...newRecords,
      },
    },
  };
};

export default reducer;
